import { useState } from "react";

// @mui material components
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Annotation Portal React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useMaterialUIController } from "context";
import { useLanguage } from "context/LanguageContext";
import { UI_LOCALE_OPTIONS } from "constants/uiLocales";
import { useTranslation } from "hooks/useTranslation";
import useCanHover from "hooks/useCanHover";

/* Portal language picker for the bottom of the sidebar. The list comes from
   UI_LOCALE_OPTIONS, so adding a locale folder + entry there is enough. Picking a
   language saves it to the user's profile (LanguageContext.setLanguage). */
function SidenavLanguageMenu() {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [controller] = useMaterialUIController();
  const { miniSidenav } = controller;
  const canHover = useCanHover();
  const [anchorEl, setAnchorEl] = useState(null);

  const current = UI_LOCALE_OPTIONS.find((o) => o.code === language) || UI_LOCALE_OPTIONS[0];
  const label = t("navbar.language", "Language");

  const handleSelect = (code) => {
    setAnchorEl(null);
    if (code !== language) setLanguage(code);
  };

  return (
    <>
      <Tooltip
        title={miniSidenav && canHover ? `${label}: ${current.label}` : ""}
        placement="right"
      >
        <MDBox
          component="button"
          type="button"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav ? "center" : "flex-start"}
          width="100%"
          mb={1.5}
          px={miniSidenav ? 0 : 1.25}
          py={1}
          sx={{
            cursor: "pointer",
            color: "#fff",
            font: "inherit",
            bgcolor: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "10px",
            transition: "background-color 180ms ease, border-color 180ms ease",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.18)",
              borderColor: "rgba(255, 255, 255, 0.28)",
            },
          }}
        >
          <Icon fontSize="small" sx={{ color: "#fff" }}>
            translate
          </Icon>
          {!miniSidenav && (
            <>
              <MDBox ml={1} flex={1} minWidth={0} textAlign="left">
                <MDTypography
                  display="block"
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.6)", lineHeight: 1.2 }}
                >
                  {label}
                </MDTypography>
                <MDTypography
                  display="block"
                  variant="button"
                  fontWeight="medium"
                  sx={{ color: "#fff" }}
                >
                  {current.label}
                </MDTypography>
              </MDBox>
              <Icon fontSize="small" sx={{ color: "#fff" }}>
                {anchorEl ? "expand_less" : "expand_more"}
              </Icon>
            </>
          )}
        </MDBox>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: miniSidenav ? "right" : "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        {UI_LOCALE_OPTIONS.map((o) => (
          <MenuItem
            key={o.code}
            selected={o.code === language}
            onClick={() => handleSelect(o.code)}
            lang={o.code}
          >
            <ListItemText primary={o.label} />
            {o.code === language && (
              <Icon fontSize="small" sx={{ ml: 2 }}>
                check
              </Icon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default SidenavLanguageMenu;
